// @flow

import React, { useCallback } from 'react';
import DocumentTitle from 'react-document-title';
import { useDispatch } from 'react-redux';
import {
  Banner,
  Button,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { ModalConfirm } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { AddIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import TableLayout from '../../components/TableLayout';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { promptToaster as promptToasterAction } from '../App/actions';

import useTourBlockouts from './hooks';
import TourBlockoutModal from './TourBlockoutModal';
import messages from './messages';

// A property's Tour Blockouts: list, add, edit and delete. Behind the
// `tourScheduling` flag and the `property-activity-manage` permission (see
// PageContent.js). Every rule is the backend's; this screen shows its answers.
const TourBlockouts = () => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const dispatch = useDispatch();
  const promptToaster = useCallback(
    (payload: Object) => dispatch(promptToasterAction(payload)),
    [dispatch],
  );
  const baseTableProps = useBaseTableProps();
  const {
    title,
    timezone,
    rows,
    headers,
    tableName,
    isLoading,
    loadError,
    emptyStateMessage,
    handleAdd,
    modalProps,
    deleteConfirmProps,
  } = useTourBlockouts({ intl, promptToaster });

  // DocumentTitle renders a single child (React.Children.only).
  return (
    <DocumentTitle title={title}>
      <>
        <TableLayout name={tableName}>
          {(tableHeight) => (
            <>
              <TableHeader
                title={title}
                count={isLoading || loadError ? undefined : rows.length}
                countName={intl.formatMessage(messages.countName)}
                actions={
                  <Button
                    variant="shout"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    disabled={Boolean(loadError)}
                  >
                    {intl.formatMessage(messages.addBlockout)}
                  </Button>
                }
              />
              {loadError ? (
                <Banner
                  status="error"
                  message={loadError}
                  sx={{ width: '100%' }}
                />
              ) : (
                <Table
                  {...baseTableProps}
                  name={tableName}
                  height={tableHeight}
                  headers={headers}
                  rows={rows}
                  isLoading={isLoading}
                  count={rows.length}
                  totalCount={rows.length}
                  showSettings={false}
                  emptyStateMessage={emptyStateMessage}
                />
              )}
            </>
          )}
        </TableLayout>
        {modalProps.open && (
          <TourBlockoutModal
            open={modalProps.open}
            tourBlockout={modalProps.tourBlockout}
            timezone={timezone}
            organizationId={selectedProperty?.organizationId}
            propertyId={selectedProperty?.id}
            intl={intl}
            handleClose={modalProps.handleClose}
            promptToaster={promptToaster}
          />
        )}
        <ModalConfirm {...deleteConfirmProps} />
      </>
    </DocumentTitle>
  );
};

export default TourBlockouts;
